/**
 * Community Page Route Definition
 */
import { createFileRoute } from '@tanstack/react-router';
import { authenticatedGetShared } from '@/lib/api/authenticated-shared';
import { CommunityRoutePending } from '@/components/v2/page-template-pending';
import type { PaginatedPostsV2 } from '@/lib/api/v2/community';
import { cacheFirst } from '@/lib/queries/cache-first';
import { createDeferredCatalogueLoaderData } from '../-catalogue-loader-data';

export const Route = createFileRoute('/_authenticated/community/')({
  // Template-first pending while the feed route chunk downloads (first visit).
  pendingComponent: CommunityRoutePending,
  loader: async ({ context }) => {
    const listKey = ['community-posts', 'all'] as const;
    // Cache-first: a cached feed streams instantly while the plane heals
    // staleness in the background (single-flight-deduped with the mounted
    // hook), so re-entry after the 5-minute staleTime never re-suspends the
    // whole results region; a cold cache keeps the streamed loader fetch.
    const { cached, stream } = cacheFirst(context.queryClient, listKey, () =>
      context.queryClient.ensureInfiniteQueryData({
        queryFn: ({ pageParam }) => {
          // Dual transport (#1290 pattern): authenticatedGetShared forwards
          // cookies through the server fn during SSR and calls the Go API
          // directly on client navigations, so the seeded cache entry and the
          // mounted query land in the identical shape under one key.
          const qs = new URLSearchParams({ tab: 'all' });
          if (pageParam) qs.set('cursor', pageParam);
          return authenticatedGetShared<PaginatedPostsV2>(`/v2/community/posts?${qs.toString()}`);
        },
        queryKey: listKey,
        initialPageParam: undefined as string | undefined,
      })
    );
    return createDeferredCatalogueLoaderData(
      cached !== undefined ? Promise.resolve(cached) : stream()
    );
  },
});
